export interface GitHubStats {
  totalStars: number;
  totalCommits: number;
  totalPRs: number;
  totalIssues: number;
  contributedRepos: number;
  publicRepos: number;
  followers: number;
  following: number;
}

export interface GitHubLanguage {
  name: string;
  percentage: number;
  color: string;
}

export interface GitHubUser {
  login: string;
  name: string;
  bio: string;
  avatar_url: string;
  public_repos: number;
  followers: number;
  following: number;
  created_at: string;
}

const GITHUB_USERNAME = "Lil-Code30";
const GITHUB_API_BASE = "https://api.github.com";

const LANGUAGE_COLORS: { [key: string]: string } = {
  JavaScript: "#f1e05a",
  TypeScript: "#3178c6",
  HTML: "#e34c26",
  CSS: "#563d7c",
  SCSS: "#c6538c",
  Python: "#3572A5",
  Java: "#b07219",
  "C++": "#f34b7d",
  "C#": "#239120",
  PHP: "#4F5D95",
  Go: "#00ADD8",
  Rust: "#dea584",
  Swift: "#fa7343",
  Kotlin: "#A97BFF",
  Dart: "#00B4AB",
  Ruby: "#701516",
  Shell: "#89e051",
  Dockerfile: "#384d54",
  Vue: "#41b883",
  React: "#61dafb",
};

export class GitHubService {
  private static reposCache: { data: any[]; timestamp: number } | null = null;
  private static readonly CACHE_DURATION = 2 * 60 * 1000;

  private static async fetchWithErrorHandling(
    url: string,
    headers?: Record<string, string>,
  ): Promise<any> {
    try {
      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error(`GitHub API Error: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("GitHub API fetch error:", error);
      return null;
    }
  }

  static async fetchUserData(): Promise<GitHubUser | null> {
    return this.fetchWithErrorHandling(
      `${GITHUB_API_BASE}/users/${GITHUB_USERNAME}`,
    );
  }

  static clearCache(): void {
    this.reposCache = null;
  }

  static async fetchUserRepos(): Promise<any[]> {
    if (
      this.reposCache &&
      Date.now() - this.reposCache.timestamp < this.CACHE_DURATION
    ) {
      return this.reposCache.data;
    }

    const repos: any[] = [];
    let url: string | null =
      `${GITHUB_API_BASE}/users/${GITHUB_USERNAME}/repos?per_page=100&sort=updated`;

    while (url) {
      try {
        const response: Response = await fetch(url);
        if (!response.ok) {
          throw new Error(`GitHub API error: ${response.status}`);
        }
        const data: any[] = await response.json();
        repos.push(...data);

        const linkHeader: string | null = response.headers.get("Link");
        url = null;
        if (linkHeader) {
          for (const link of linkHeader.split(",")) {
            if (link.includes('rel="next"')) {
              const match: RegExpMatchArray | null = link.match(/<([^>]+)>/);
              if (match && match[1]) url = match[1];
              break;
            }
          }
        }
      } catch (error) {
        console.error("GitHub API fetch error:", error);
        break;
      }
    }

    this.reposCache = { data: repos, timestamp: Date.now() };
    return repos;
  }

  static async fetchLanguagesData(): Promise<GitHubLanguage[]> {
    try {
      const repos = await this.fetchUserRepos();
      if (repos.length === 0) {
        return this.getFallbackLanguages();
      }

      const languageStats: { [key: string]: number } = {};
      let totalBytes = 0;

      for (const repo of repos) {
        const langData = await this.fetchWithErrorHandling(repo.languages_url);
        if (langData) {
          Object.entries(langData).forEach(([lang, bytes]) => {
            languageStats[lang] =
              (languageStats[lang] || 0) + (bytes as number);
            totalBytes += bytes as number;
          });
        }
      }

      const languages = Object.entries(languageStats)
        .map(([name, bytes]) => ({
          name,
          percentage: (bytes / totalBytes) * 100,
          color: LANGUAGE_COLORS[name] || "#8cc8ff",
        }))
        .sort((a, b) => b.percentage - a.percentage);

      return languages.length > 0 ? languages : this.getFallbackLanguages();
    } catch (error) {
      console.error("Error fetching languages:", error);
      return this.getFallbackLanguages();
    }
  }

  static async fetchGitHubStats(): Promise<GitHubStats> {
    try {
      const [userData, repos] = await Promise.all([
        this.fetchUserData(),
        this.fetchUserRepos(),
      ]);

      if (!userData) {
        return this.getFallbackStats();
      }

      const totalStars = repos.reduce(
        (sum: number, repo: any) => sum + (repo.stargazers_count || 0),
        0,
      );

      const contributedRepos = repos.filter((repo: any) => repo.fork).length;

      const [totalPRs, totalIssues, totalCommits] = await Promise.all([
        this.fetchTotalPRs(),
        this.fetchTotalIssues(),
        this.fetchTotalCommits(),
      ]);

      return {
        totalStars,
        totalCommits,
        totalPRs,
        totalIssues,
        contributedRepos,
        publicRepos: userData.public_repos,
        followers: userData.followers,
        following: userData.following,
      };
    } catch (error) {
      console.error("Error fetching GitHub stats:", error);
      return this.getFallbackStats();
    }
  }

  private static async fetchTotalPRs(): Promise<number> {
    const data = await this.fetchWithErrorHandling(
      `${GITHUB_API_BASE}/search/issues?q=author:${GITHUB_USERNAME}+type:pr`,
    );
    return data?.total_count ?? 0;
  }

  private static async fetchTotalIssues(): Promise<number> {
    const data = await this.fetchWithErrorHandling(
      `${GITHUB_API_BASE}/search/issues?q=author:${GITHUB_USERNAME}+type:issue`,
    );
    return data?.total_count ?? 0;
  }

  private static async fetchTotalCommits(): Promise<number> {
    const data = await this.fetchWithErrorHandling(
      `${GITHUB_API_BASE}/search/commits?q=author:${GITHUB_USERNAME}`,
      { Accept: "application/vnd.github.cloak-preview" },
    );
    return data?.total_count ?? 0;
  }

  private static getFallbackStats(): GitHubStats {
    return {
      totalStars: 28,
      totalCommits: 1400,
      totalPRs: 11,
      totalIssues: 0,
      contributedRepos: 8,
      publicRepos: 30,
      followers: 25,
      following: 15,
    };
  }

  private static getFallbackLanguages(): GitHubLanguage[] {
    return [
      { name: "JavaScript", percentage: 32.95, color: "#f1e05a" },
      { name: "HTML", percentage: 30.47, color: "#e34c26" },
      { name: "CSS", percentage: 16.75, color: "#563d7c" },
      { name: "TypeScript", percentage: 9.65, color: "#3178c6" },
      { name: "SCSS", percentage: 7.39, color: "#c6538c" },
      { name: "Python", percentage: 1.54, color: "#3572A5" },
    ];
  }
}
